import { useState, useRef, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@mui/system';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MicIcon from '@mui/icons-material/Mic';
import Typography from '@mui/material/Typography';
import StopIcon from '@mui/icons-material/Stop';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import Notification from '../Notification';
import { useSurveyResponse } from './SurveyResponseContext';


const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1); 
  }
  100% {
    transform: scale(1);
  }
`;

const AudioRecorder = ({ onDataSend, idx }) => {
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState('');
  const [alert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const location = useLocation();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const { t } = useTranslation();
  const { responses } = useSurveyResponse();

  useEffect(() => {
    // Reset audio URL when the index changes
    if (location.state && location.state.idx !== idx) {
      setAudioURL('');
      audioChunksRef.current = [];
      mediaRecorderRef.current = null;
      setRecording(false);
    }
  }, [location.state]);

  useEffect(() => {
    // Check if there is an existing response for the current index
    const existingResponse = responses.find(resp => resp.idx === idx);
    if (existingResponse && existingResponse.audioResponse) {
      const audioUrl = URL.createObjectURL(existingResponse.audioResponse);
      setAudioURL(audioUrl);
    } else {
      setAudioURL('');
    }
  }, [idx]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioURL(audioUrl);
        onDataSend(audioBlob); // Send audio Blob to parent component

        stream?.getTracks().forEach(track => track.stop()); // Stop all tracks
      };

      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (err) {
      console.error('Microphone access denied or error:', err);
      setAlertData({ 
        severity: 'error', 
        message: t('survey.microphone_access_error')
      });
      setAlert(true);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const handleDeleteRecording = () => {
    if (!audioURL) return; // No recording to delete
    setOpenDialog(true);
  };
  
  const confirmDeleteRecording = () => {
    setAudioURL('');
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    setRecording(false);
    onDataSend(null); // Notify parent component that recording is deleted
    setOpenDialog(false);
  }

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, border: '1px solid #ccc', padding: { xs: '10px', sm: '20px' }, borderRadius: '8px' }}>
      {/* Alert */}
      {alert && (
        <Notification 
          severity={alertData.severity} 
          message={alertData.message} 
          onClose={() => setAlert(false)}
          scroll={true}
        />
      )}
      <Button 
        sx={{ borderRadius: '50%', backgroundColor: '#f2140c', width: '60px', height: '60px', mb: 2,
          animation: recording ? `${pulse} 1.5s infinite ease-in-out` : 'none',
          transition: 'transform 0.2s',
         }}
        onClick={recording ? stopRecording : startRecording}
      >
        {recording ? <StopIcon sx={{ color: '#ffff' }} /> : <MicIcon sx={{ color: '#ffff'}} />}
      </Button>
      <Typography variant='body1' sx={{ mb: { xs: 0, sm: 2 }, textAlign: 'center' }}>{recording ? t('survey.recording_stop') : t('survey.recording_start')}</Typography>
      {audioURL && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <audio controls src={audioURL} />
            <Tooltip title={t('survey.delete_recording')}>
              <IconButton aria-label="delete" sx={{ ml: 1 }} onClick={handleDeleteRecording}>
                <DeleteForeverIcon sx={{ color: 'red', fontSize: '1.5rem', }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <DialogTitle id="confirm-dialog-title">
              {t('survey.delete_recording')}?
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="confirm-dialog-description">
                {t('survey.delete_recording_confirm')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>{t('survey.cancel')}</Button>
              <Button onClick={confirmDeleteRecording} autoFocus>
                {t('survey.delete')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default AudioRecorder;